/**
 * Knowledge CMS API client.
 * Follows the same fetchWithAuth pattern as ragApi.ts.
 */
import { acquireToken } from './tokenHelper';

const API_URL = (window as any).__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || '/api';

async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  const token = await acquireToken();
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text();
    let message: string;
    try { const json = JSON.parse(body); message = json.error || json.message || response.statusText; }
    catch { message = body || response.statusText; }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface KbArticle {
  id: string;
  slug: string;
  title: string;
  content: string;
  summary: string;
  contentHash: string;
  scope: 'system' | 'agent' | 'team' | 'user';
  scopeId: string;
  visibility: 'public' | 'scoped';
  category: string;
  tags: string[];
  relatesTo: string[];
  origin: 'pipeline' | 'user' | 'agent' | 'import';
  sourceUrl?: string;
  author: string;
  contributors: string[];
  status: 'draft' | 'published' | 'archived';
  reviewedBy?: string;
  reviewedAt?: string;
  indexStatus: 'pending' | 'indexing' | 'ready' | 'stale' | 'error';
  chunksCount: number;
  indexedAt?: string;
  errorMessage?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface KbVersion {
  id: string;
  articleId: string;
  version: number;
  content: string;
  contentHash: string;
  editedBy: string;
  editSummary?: string;
  createdAt: string;
}

export interface ListArticlesResult {
  articles: KbArticle[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CategoryCount {
  category: string;
  count: number;
}

export interface TagCount {
  tag: string;
  count: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Article CRUD
// ─────────────────────────────────────────────────────────────────────────────

export async function listArticles(params?: {
  scope?: string;
  scopeId?: string;
  category?: string;
  tag?: string;
  status?: string;
  origin?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}): Promise<ListArticlesResult> {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') query.set(k, String(v));
    });
  }
  const qs = query.toString();
  const res = await fetchWithAuth(`${API_URL}/kb/articles${qs ? `?${qs}` : ''}`);
  return handleResponse<ListArticlesResult>(res);
}

export async function getArticle(idOrSlug: string): Promise<KbArticle> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${encodeURIComponent(idOrSlug)}`);
  return handleResponse<KbArticle>(res);
}

export async function createArticle(body: {
  title: string;
  content: string;
  summary?: string;
  slug?: string;
  scope?: string;
  scopeId?: string;
  category?: string;
  tags?: string[];
  status?: string;
}): Promise<KbArticle> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return handleResponse<KbArticle>(res);
}

export async function updateArticle(id: string, body: {
  title?: string;
  content?: string;
  summary?: string;
  category?: string;
  tags?: string[];
  relatesTo?: string[];
  editSummary?: string;
}): Promise<KbArticle> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return handleResponse<KbArticle>(res);
}

export async function deleteArticle(id: string): Promise<void> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || res.statusText);
  }
}

export async function publishArticle(id: string): Promise<KbArticle> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${id}/publish`, { method: 'POST' });
  return handleResponse<KbArticle>(res);
}

export async function archiveArticle(id: string): Promise<void> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${id}/archive`, { method: 'POST' });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || res.statusText);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Versioning
// ─────────────────────────────────────────────────────────────────────────────

export async function getHistory(articleId: string): Promise<KbVersion[]> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${articleId}/history`);
  const data = await handleResponse<{ versions: KbVersion[] }>(res);
  return data.versions;
}

export async function getVersion(articleId: string, version: number): Promise<KbVersion> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${articleId}/history/${version}`);
  return handleResponse<KbVersion>(res);
}

export async function revertVersion(articleId: string, version: number): Promise<KbArticle> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${articleId}/revert/${version}`, { method: 'POST' });
  return handleResponse<KbArticle>(res);
}

// ─────────────────────────────────────────────────────────────────────────────
// Taxonomy
// ─────────────────────────────────────────────────────────────────────────────

export async function getCategories(): Promise<CategoryCount[]> {
  const res = await fetchWithAuth(`${API_URL}/kb/categories`);
  const data = await handleResponse<{ categories: CategoryCount[] }>(res);
  return data.categories;
}

export async function getTags(): Promise<TagCount[]> {
  const res = await fetchWithAuth(`${API_URL}/kb/tags`);
  const data = await handleResponse<{ tags: TagCount[] }>(res);
  return data.tags;
}

// ─────────────────────────────────────────────────────────────────────────────
// Health
// ─────────────────────────────────────────────────────────────────────────────

export interface KbHealthSummary {
  totalArticles: number;
  published: number;
  draft: number;
  archived: number;
  stale: number;
  indexErrors: number;
  categories: string[];
  lastSeedAt: string | null;
}

export async function getKbHealth(): Promise<KbHealthSummary> {
  const res = await fetchWithAuth(`${API_URL}/kb/health`);
  return handleResponse<KbHealthSummary>(res);
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF Export
// ─────────────────────────────────────────────────────────────────────────────

export async function downloadArticlePdf(idOrSlug: string, title?: string): Promise<void> {
  const res = await fetchWithAuth(`${API_URL}/kb/articles/${encodeURIComponent(idOrSlug)}/pdf`);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (title || idOrSlug).replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '-').slice(0, 80) + '.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Search
// ─────────────────────────────────────────────────────────────────────────────

export async function searchKb(query: string, options?: {
  scope?: string;
  category?: string;
  agentId?: string;
  limit?: number;
}): Promise<any[]> {
  const res = await fetchWithAuth(`${API_URL}/kb/search`, {
    method: 'POST',
    body: JSON.stringify({ query, ...options }),
  });
  const data = await handleResponse<{ results: any[] }>(res);
  return data.results;
}
